import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  type Unsubscribe,
} from "firebase/firestore";
import { onValue, ref } from "firebase/database";
import { httpsCallable } from "firebase/functions";
import {
  CALLABLE_FUNCTIONS,
  type DeviceDocument,
  type DevicePolicyDocument,
  type EncodingPreference,
  type MintSessionRequest,
  type MintSessionResponse,
  type RevokeDeviceRequest,
  type RevokeDeviceResponse,
  type SessionMode,
  type UpdateDevicePolicyRequest,
  type UpdateDevicePolicyResponse,
} from "@srd/shared";
import { useAuth } from "../auth/AuthProvider";
import { db, functions, rtdb } from "../firebase";
import { stashSessionSecrets } from "../session/sessionSecrets";

type PresenceMap = Record<string, { status?: string; updatedAt?: number }>;

export function DeviceListPage() {
  const { user, claims, signOutUser, startMfaPlaceholder, refreshClaims } = useAuth();
  const navigate = useNavigate();
  const [devices, setDevices] = useState<DeviceDocument[]>([]);
  const [presence, setPresence] = useState<PresenceMap>({});
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [mode, setMode] = useState<SessionMode>("attended");
  const [encoding, setEncoding] = useState<EncodingPreference>("ui_tile");
  const [recordingConsent, setRecordingConsent] = useState(false);
  const [mfaUrl, setMfaUrl] = useState<string | null>(null);
  const [policyDeviceId, setPolicyDeviceId] = useState("");
  const [allowAttended, setAllowAttended] = useState(true);
  const [allowUnattended, setAllowUnattended] = useState(false);
  const [policyGroups, setPolicyGroups] = useState("");
  const [policyStatus, setPolicyStatus] = useState<string | null>(null);
  const [revokeReason, setRevokeReason] = useState("");
  const isAdmin = claims?.role === "admin";

  useEffect(() => {
    if (!claims?.tenantId) return;
    const q = query(
      collection(db, "devices"),
      where("tenantId", "==", claims.tenantId),
    );
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => d.data() as DeviceDocument);
        rows.sort((a, b) => a.displayName.localeCompare(b.displayName));
        setDevices(rows);
      },
      (err) => setError(err.message),
    );
    return () => unsub();
  }, [claims?.tenantId]);

  useEffect(() => {
    const presenceRef = ref(rtdb, "presence/devices");
    const unsub = onValue(
      presenceRef,
      (snap) => {
        setPresence((snap.val() as PresenceMap) ?? {});
      },
      () => {
        // Presence optional when RTDB empty / rules block auditors
      },
    );
    return () => unsub();
  }, []);

  async function loadPolicy(deviceId: string) {
    setPolicyDeviceId(deviceId);
    setPolicyStatus("Loading policy…");
    try {
      const snap = await getDoc(doc(db, "devicePolicies", deviceId));
      if (!snap.exists()) {
        setAllowAttended(true);
        setAllowUnattended(false);
        setPolicyGroups("");
        setPolicyStatus("No policy doc yet (defaults until saved).");
        return;
      }
      const p = snap.data() as DevicePolicyDocument;
      setAllowAttended(p.allowAttended);
      setAllowUnattended(p.allowUnattended);
      setPolicyGroups((p.unattendedAllowedGroups ?? []).join(", "));
      setPolicyStatus(`Loaded policy updatedAt=${p.updatedAt}`);
    } catch (err) {
      setPolicyStatus(String(err));
    }
  }

  async function savePolicy() {
    if (!policyDeviceId) {
      setPolicyStatus("Select a device first.");
      return;
    }
    setPolicyStatus("Saving policy…");
    try {
      const callable = httpsCallable<
        UpdateDevicePolicyRequest,
        UpdateDevicePolicyResponse
      >(functions, CALLABLE_FUNCTIONS.updateDevicePolicy);
      const result = await callable({
        deviceId: policyDeviceId,
        allowAttended,
        allowUnattended,
        unattendedAllowedGroups: policyGroups
          ? policyGroups.split(",").map((g) => g.trim()).filter(Boolean)
          : [],
      });
      setPolicyStatus(
        `Saved deviceId=${result.data.deviceId} at ${result.data.updatedAt}`,
      );
    } catch (err) {
      setPolicyStatus(String(err));
    }
  }

  async function revokeDevice(deviceId: string) {
    if (!confirm(`Revoke device ${deviceId}? Agent stops within ~30s.`)) {
      return;
    }
    setError(null);
    try {
      const callable = httpsCallable<RevokeDeviceRequest, RevokeDeviceResponse>(
        functions,
        CALLABLE_FUNCTIONS.revokeDevice,
      );
      const result = await callable({
        deviceId,
        reason: revokeReason || undefined,
      });
      setError(null);
      setPolicyStatus(
        `Revoked ${result.data.deviceId} at ${result.data.revokedAt}`,
      );
    } catch (err) {
      setError(String(err));
    }
  }

  async function startSession(device: DeviceDocument) {
    setError(null);
    if (mode === "unattended" && !claims?.canUnattended) {
      setError("Your account lacks canUnattended claim.");
      return;
    }
    setBusyId(device.deviceId);
    try {
      const callable = httpsCallable<MintSessionRequest, MintSessionResponse>(
        functions,
        CALLABLE_FUNCTIONS.mintSession,
      );
      const result = await callable({
        deviceId: device.deviceId,
        mode,
        encodingPreference: encoding,
        recordingConsent,
      });
      const data = result.data;
      if (!data.turn) {
        throw new Error("mintSession did not return TURN credentials.");
      }
      stashSessionSecrets({
        sessionId: data.sessionId,
        sessionJwt: data.sessionJwt,
        turn: data.turn,
        deviceId: device.deviceId,
        deviceName: device.displayName,
        mode,
        encodingPreference: encoding,
        expiresAt: data.expiresAt,
      });
      navigate(`/session/${data.sessionId}`);
    } catch (err) {
      setError(String(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="page">
      <header className="topbar">
        <div>
          <h1>Devices</h1>
          <p className="muted">
            {user?.email ?? user?.uid}
            {claims
              ? ` · ${claims.role} · tenant ${claims.tenantId}`
              : " · missing support claims"}
          </p>
        </div>
        <div className="row">
          <Link to="/enrollment">Enrollment</Link>
          <button
            type="button"
            onClick={() => {
              void startMfaPlaceholder()
                .then(setMfaUrl)
                .catch((err) => setError(String(err)));
            }}
          >
            MFA placeholder
          </button>
          <button type="button" onClick={() => void refreshClaims()}>
            Refresh claims
          </button>
          <button type="button" onClick={() => void signOutUser()}>
            Sign out
          </button>
        </div>
      </header>

      {!claims ? (
        <section className="panel">
          <p className="status err">
            Signed in but missing support custom claims (<code>role</code>,{" "}
            <code>tenantId</code>). Run{" "}
            <code>scripts/set-support-claims.mjs</code> for your email, then click Refresh
            claims (or sign out/in).
          </p>
        </section>
      ) : null}

      <section className="panel">
        <h2>Start attended session</h2>
        <div className="row">
          <label>
            Mode
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as SessionMode)}
            >
              <option value="attended">attended</option>
              <option value="unattended">unattended</option>
            </select>
          </label>
          <label>
            Encoding preference
            <select
              value={encoding}
              onChange={(e) => setEncoding(e.target.value as EncodingPreference)}
            >
              <option value="ui_tile">ui_tile (default)</option>
              <option value="h264_hw">h264_hw (Video)</option>
              <option value="auto">auto</option>
            </select>
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={recordingConsent}
              onChange={(e) => setRecordingConsent(e.target.checked)}
            />
            Request session recording
          </label>
        </div>
        <ul className="hints">
          <li>Attended sessions wait for customer Accept (`pending_accept` → `active`).</li>
          <li>
            Unattended requires <code>canUnattended</code>, device policy allowlist, enrolled key,
            and <code>CUSTOMER_UNATTENDED_ENABLED</code> (pen-test gate; default off).
          </li>
          <li>
            Recording checkbox sets consent metadata only — no media muxer yet (
            <code>docs/session-recording.md</code>).
          </li>
          <li>Session JWT + TURN stay in memory only (sessionStorage cleared on tab close).</li>
        </ul>
      </section>

      <section className="panel">
        <h2>Device list</h2>
        {devices.length === 0 ? (
          <p className="status">No devices in this tenant. Create an enrollment token first.</p>
        ) : (
          <table className="device-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Platform</th>
                <th>Presence</th>
                <th>Groups</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => {
                const p = presence[device.deviceId];
                return (
                  <tr key={device.deviceId}>
                    <td>
                      <div className="device-name">{device.displayName}</div>
                      <div className="muted tiny">{device.deviceId}</div>
                    </td>
                    <td>{device.platform}</td>
                    <td>
                      <span className={`badge presence-${p?.status ?? "unknown"}`}>
                        {p?.status ?? "unknown"}
                      </span>
                    </td>
                    <td>{device.groups?.join(", ") || "—"}</td>
                    <td>
                      <span className="badge">{device.status}</span>
                    </td>
                    <td>
                      <div className="row">
                        <button
                          type="button"
                          className="primary"
                          disabled={
                            busyId === device.deviceId ||
                            !claims ||
                            device.status !== "active"
                          }
                          onClick={() => void startSession(device)}
                        >
                          {busyId === device.deviceId ? "Starting…" : "Connect"}
                        </button>
                        {isAdmin ? (
                          <button
                            type="button"
                            onClick={() => void loadPolicy(device.deviceId)}
                          >
                            Policy
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {isAdmin ? (
        <section className="panel">
          <h2>Unattended policy &amp; revoke</h2>
          <div className="row">
            <input
              value={policyDeviceId}
              onChange={(e) => setPolicyDeviceId(e.target.value)}
              placeholder="deviceId"
            />
            <label className="check">
              <input
                type="checkbox"
                checked={allowAttended}
                onChange={(e) => setAllowAttended(e.target.checked)}
              />
              attended
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={allowUnattended}
                onChange={(e) => setAllowUnattended(e.target.checked)}
              />
              unattended
            </label>
            <input
              value={policyGroups}
              onChange={(e) => setPolicyGroups(e.target.value)}
              placeholder="unattendedAllowedGroups (comma)"
              style={{ minWidth: "16rem" }}
            />
            <button type="button" className="primary" onClick={() => void savePolicy()}>
              Save policy
            </button>
          </div>
          <p className="muted tiny">
            Techs need overlapping <code>deviceGroups</code> claims (admins bypass). Agent
            honors policy on each heartbeat.
          </p>
          <div className="row" style={{ marginTop: "0.75rem" }}>
            <input
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              placeholder="revoke reason"
            />
            <button
              type="button"
              disabled={!policyDeviceId}
              onClick={() => void revokeDevice(policyDeviceId)}
            >
              Revoke device
            </button>
          </div>
          {policyStatus ? <p className="status ok">{policyStatus}</p> : null}
        </section>
      ) : null}

      {error ? <p className="status err">{error}</p> : null}
      {mfaUrl ? <p className="status ok">{mfaUrl}</p> : null}
      <p className="muted tiny">
        <Link to="/login">Auth</Link> · viewer protocol in docs/PROTOCOL.md
      </p>
    </div>
  );
}
